import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LightTaskTestability } from '@cortex/domain-product-innovation-lab-types';
import { projectApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  PerformanceAggMode,
  PmoDeadlineUpdateInput,
  LightTaskPoolResponse,
  AssignedLightTaskResponse,
  AssignedLightTaskItem,
} from '../types/project.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板「期限管理」ブロック用フック。結合期限切れ・結合完了日未設定の進行中Gen2プロジェクトをTanStack Queryで取得
 * @graph-connects /projects/pmo-board/overdue [calls] PMO期限切れ案件取得API
 * @graph-connects projectApi.getPmoOverdue [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function usePmoOverdueProjects() {
  return useQuery({
    queryKey: queryKeys.projects.pmoOverdue(),
    queryFn: () => projectApi.getPmoOverdue(),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板「期限直近」ブロック用フック。今日から指定日数以内に結合期限を迎え、結合完了日未設定の進行中Gen2プロジェクトをTanStack Queryで取得
 * @graph-connects /projects/pmo-board/upcoming [calls] PMO直近期限案件取得API
 * @graph-connects projectApi.getPmoUpcoming [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function usePmoUpcomingProjects(daysAhead = 3) {
  return useQuery({
    queryKey: queryKeys.projects.pmoUpcoming(daysAhead),
    queryFn: () => projectApi.getPmoUpcoming(daysAhead),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板「リリース日漏れ管理」ブロック用フック。リリース完了日未設定 + 親チケ完了 or リリース子チケ完了(親チケ完了以外) のチケットを TanStack Query で取得
 * @graph-connects /projects/pmo-board/release-leaks [calls] リリース日漏れ取得API
 * @graph-connects projectApi.getPmoReleaseLeaks [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function usePmoReleaseLeaks() {
  return useQuery({
    queryKey: queryKeys.projects.pmoReleaseLeaks(),
    queryFn: () => projectApi.getPmoReleaseLeaks(),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板「アサイン日漏れ管理」ブロック用フック。アサイン決定日未設定 + リリース済 or 未対応/保留中以外 のチケットを TanStack Query で取得
 * @graph-connects /projects/pmo-board/assign-leaks [calls] アサイン日漏れ取得API
 * @graph-connects projectApi.getPmoAssignLeaks [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function usePmoAssignLeaks() {
  return useQuery({
    queryKey: queryKeys.projects.pmoAssignLeaks(),
    queryFn: () => projectApi.getPmoAssignLeaks(),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PL/主担当看板用フック。ログインユーザーが PL (assignee) または 主担当 (main_worker) になっている進行中Gen2案件一覧を TanStack Query で取得
 * @graph-connects /projects/my-projects [calls] PL/主担当看板取得API
 * @graph-connects projectApi.getMyProjectsBoard [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMyProjectsBoard() {
  return useQuery({
    queryKey: queryKeys.projects.myProjectsBoard(),
    queryFn: () => projectApi.getMyProjectsBoard(),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 軽タスクリスト（全社共有プール）用フック。AI 判定で軽タスク認定され開発リーダー未設定の案件を起票時刻の昇順で取得
 * @graph-connects /projects/light-tasks [calls] 軽タスクリスト取得API
 * @graph-connects projectApi.getLightTaskPool [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useLightTaskPool() {
  return useQuery({
    queryKey: queryKeys.projects.lightTaskPool(),
    queryFn: () => projectApi.getLightTaskPool(),
  });
}

/**
 * アサイン済み軽タスク一覧取得フック。軽タスク認定・開発リーダー設定済み・未完了の案件を起票時刻昇順で取得する。
 * 全メンバーが閲覧可能。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロジェクト管理配下の軽タスクリストページ（`/projects/light-tasks`）の「アサイン済み軽タスク」ブロック用フック。フラグ on・開発リーダー設定済み・未完了の案件を取得
 * @graph-connects /projects/light-tasks/assigned [calls] アサイン済み軽タスク取得API
 * @graph-connects projectApi.getAssignedLightTasks [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useAssignedLightTasks() {
  return useQuery({
    queryKey: queryKeys.projects.assignedLightTasks(),
    queryFn: () => projectApi.getAssignedLightTasks(),
  });
}

/**
 * 軽タスク セルフ・アサイン（軽タスクをリクエストする）ミューテーション。押した本人の所属に合う
 * 軽タスクを起票順に1件アサインする。成功時は待機列から取り除きアサイン済みへ楽観追加する
 * （BQ 同期にはラグがあるため即時反映し、以降の自然な refetch で整合させる。ここでは即時
 * invalidate しない＝取得直後に待機列へ戻るのを避ける）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 軽タスク セルフ・アサインのミューテーション。成功時に待機列→アサイン済みへ楽観的にカードを移動する
 * @graph-connects /projects/light-tasks/claim [calls] セルフ・アサインAPI
 * @graph-connects projectApi.claimLightTask [calls] APIクライアント呼び出し
 */
export function useClaimLightTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => projectApi.claimLightTask(),
    onSuccess: (result) => {
      const assigned = result.assigned;
      if (!assigned) return;
      // 待機列から当該カードを取り除きつつ、移動元の付帯情報（やりたいこと/PL/チーム/起票日）を控える。
      let moved: LightTaskPoolResponse['items'][number] | undefined;
      queryClient.setQueryData<LightTaskPoolResponse>(
        queryKeys.projects.lightTaskPool(),
        (prev) => {
          if (!prev) return prev;
          moved = prev.items.find((item) => item.issueKey === assigned.issueKey);
          const items = prev.items.filter((item) => item.issueKey !== assigned.issueKey);
          return { items, totalCount: items.length };
        },
      );
      // アサイン済みへ楽観追加（起票時刻の昇順を保つ）。
      queryClient.setQueryData<AssignedLightTaskResponse>(
        queryKeys.projects.assignedLightTasks(),
        (prev) => {
          const newItem: AssignedLightTaskItem = {
            issueKey: assigned.issueKey,
            summary: assigned.summary,
            purpose: moved?.purpose ?? null,
            developer: assigned.developer,
            pl: moved?.pl ?? null,
            team: moved?.team ?? null,
            status: '未対応',
            createdAt: moved?.createdAt ?? null,
            backlogUrl: assigned.backlogUrl,
            testability: null,
          };
          const items = [...(prev?.items ?? []), newItem].sort((a, b) =>
            (a.createdAt ?? '').localeCompare(b.createdAt ?? ''),
          );
          return { items, totalCount: items.length };
        },
      );
    },
  });
}

/**
 * 軽タスク「テスト可否」更新ミューテーション。プルダウンで選んだ値を保存する。
 *
 * 保存前に一覧キャッシュを楽観更新して即座に色を切り替え、失敗したら元の値へ巻き戻す
 * （保存できていないのに反映済みに見える状態を残さない）。サーバー確定値で上書きするため
 * 成功時にレスポンス値で再度セットする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 軽タスクのテスト可否更新ミューテーション。一覧キャッシュを楽観更新し、失敗時は元の値へ巻き戻す
 * @graph-connects /projects/light-tasks/:issueKey/testability [calls] テスト可否更新API
 * @graph-connects projectApi.updateLightTaskTestability [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useUpdateLightTaskTestability() {
  const queryClient = useQueryClient();

  const patchCache = (issueKey: string, testability: LightTaskTestability | null): void => {
    queryClient.setQueryData<AssignedLightTaskResponse>(
      queryKeys.projects.assignedLightTasks(),
      (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.issueKey === issueKey ? { ...item, testability } : item,
          ),
        };
      },
    );
  };

  return useMutation({
    mutationFn: ({
      issueKey,
      testability,
    }: {
      issueKey: string;
      testability: LightTaskTestability | null;
    }) => projectApi.updateLightTaskTestability(issueKey, testability),
    onMutate: ({ issueKey, testability }) => {
      const previous =
        queryClient
          .getQueryData<AssignedLightTaskResponse>(queryKeys.projects.assignedLightTasks())
          ?.items.find((item) => item.issueKey === issueKey)?.testability ?? null;
      patchCache(issueKey, testability);
      return { issueKey, previous };
    },
    onError: (_error, _variables, context) => {
      if (context) patchCache(context.issueKey, context.previous);
    },
    onSuccess: (result) => {
      patchCache(result.issueKey, result.testability);
    },
  });
}

/**
 * PMO看板「実績集計」用フック。指定期間(完了更新日基準)・モード(dev/design)の完了pt実績を
 * 母票の事業単位で集計する。`enabled` が true のときのみ実行する（集計ボタン押下で起動）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板「実績集計」ブロック用フック。指定期間・モードの完了pt実績を母票の事業単位で集計するクエリを enabled 制御で実行
 * @graph-connects /projects/pmo-board/performance-agg [calls] 実績集計API
 * @graph-connects projectApi.getPerformanceAggregation [calls] APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function usePerformanceAggregation(
  mode: PerformanceAggMode,
  startDate: string,
  endDate: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.projects.performanceAgg(mode, startDate, endDate),
    queryFn: () => projectApi.getPerformanceAggregation(mode, startDate, endDate),
    enabled: enabled && startDate !== '' && endDate !== '',
    // 集計はオンデマンド実行。フォーカス復帰での再フェッチは不要。
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板の期限編集ミューテーション。各期限カスタムフィールドをBacklog本体へPATCHし、成功時はPMO看板リストを再フェッチ
 * @graph-connects /projects/:issueKey/deadlines [calls] Backlog期限編集API
 * @graph-connects projectApi.updateDeadlines [calls] APIクライアント呼び出し
 */
export function useUpdateProjectDeadlines() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ issueKey, body }: { issueKey: string; body: PmoDeadlineUpdateInput }) =>
      projectApi.updateDeadlines(issueKey, body),
    onSuccess: () => {
      // 「期限超過」と「期限直近」両方のタブから編集される可能性があるため、
      // projects 配下のキャッシュをまとめて invalidate する。
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}
